// src/pages/users/UserDetail.jsx
import {
  AccountCircle as AccountIcon,
  Business as BusinessIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  Email as EmailIcon,
  Event as EventIcon,
  Fingerprint as IDIcon,
  Lock as LockIcon,
  MoreVert as MoreIcon,
  NavigateNext as NavigateNextIcon,
  Phone as PhoneIcon,
  LockPerson as SecurityIcon,
  Settings as SettingsIcon,
  LockOpen as UnlockIcon
} from '@mui/icons-material';
import {
  Alert,
  Avatar,
  Box,
  Breadcrumbs,
  Button,
  Chip,
  Divider,
  Grid,
  Link,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Menu,
  MenuItem,
  Paper,
  Tab,
  Tabs,
  Typography
} from '@mui/material';
import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

// Custom components
import { Card, MagicLoader } from '../../design-system';
import ConfirmDialog from '../../components/ui/ConfirmDialog';
import SecureAvatar from '../../components/ui/SecureAvatar';
import StatusChip from '../../components/ui/StatusChip';
import TabPanel from '../../components/ui/TabPanel';
import useAuthStore from '../../stores/authStore';
import useUsersStore from '../../stores/userStore';
import useRoleStore from '../../stores/roleStore';
import UserDetailPermissions from './UserDetailPermissions';

// User role configuration
const USER_ROLES = {
  'superadmin': { label: 'Super Admin', color: 'error' },
  'admin': { label: 'Admin', color: 'error' },
  'agency-admin': { label: 'Agency Admin', color: 'warning' },
  'agency-manager': { label: 'Agency Manager', color: 'warning' },
  'content-creator': { label: 'Content Creator', color: 'info' },
  'content-approver': { label: 'Content Approver', color: 'info' },
  'client-admin': { label: 'Client Admin', color: 'primary' },
  'client-manager': { label: 'Client Manager', color: 'primary' },
  'client-user': { label: 'Client User', color: 'primary' },
  'user': { label: 'User', color: 'default' },
};

// Format date to readable string
const formatDate = (dateString) => {
  if (!dateString) return 'N/A';
  const date = new Date(dateString);
  return date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
};

const UserDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  
  // State
  const [activeTab, setActiveTab] = useState(0);
  const [menuAnchorEl, setMenuAnchorEl] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [statusDialogOpen, setStatusDialogOpen] = useState(false);
  const [newStatus, setNewStatus] = useState(null);
  
  // Stores
  const {
    selectedUser,
    loading,
    criticalError,
    fetchUserById,
    deleteUser,
    updateUserStatus,
    clearSelectedUser,
    clearError
  } = useUsersStore();
  
  const { user: currentUser, hasPermission } = useAuthStore();
  const { roles, fetchRoles } = useRoleStore();
  
  // Permissions
  const canUpdate = hasPermission('users', 'update');
  const canDelete = hasPermission('users', 'delete');
  
  // Load user data and fetch roles
  useEffect(() => {
    fetchUserById(id);
        console.log("🚀 ~ useEffect ~ id:", id)

    // Fetch roles if not loaded
    if (roles.length === 0) {
      fetchRoles();
    }
    
    // Cleanup on unmount
    return () => {
      clearSelectedUser();
    };
  }, [id, fetchRoles]);
  
  // Handle tab change
  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };
  
  // Handle menu open/close
  const handleMenuOpen = (event) => {
    setMenuAnchorEl(event.currentTarget);
  };
  
  const handleMenuClose = () => {
    setMenuAnchorEl(null);
  };
  
  // Handle edit user
  const handleEditUser = () => {
    handleMenuClose();
    navigate(`/users/${id}/edit`);
  };
  
  // Handle delete user
  const handleDeleteClick = () => {
    handleMenuClose();
    setDeleteDialogOpen(true);
  };
  
  const handleDeleteConfirm = async () => {
    try {
      await deleteUser(id);
      setDeleteDialogOpen(false);
      navigate('/users');
    } catch (error) {
      console.log('Error deleting user:', error);
      // Keep the dialog open on error so user can see something went wrong
      // The error notification is already shown by the store
    }
  };
  
  // Handle status change
  const handleStatusClick = (active) => {
    handleMenuClose();
    setNewStatus(active);
    setStatusDialogOpen(true);
  };
  
  const handleStatusConfirm = async () => {
    try {
      await updateUserStatus(id, newStatus);
      setStatusDialogOpen(false);
    } catch (error) {
      console.log('Error updating user status:', error);
    }
  };
  
  // Get initials from name
  const getInitials = (firstName, lastName) => {
    return `${firstName?.charAt(0) || ''}${lastName?.charAt(0) || ''}`.toUpperCase();
  };
  
  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', my: 8 }}>
        <MagicLoader />
      </Box>
    );
  }
  
  if (criticalError) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert 
          severity="error" 
          onClose={() => clearError()}
          action={
            <Button 
              color="inherit" 
              size="small"
              onClick={() => {
                clearError();
                fetchUserById(id);
              }}
            >
              Retry
            </Button>
          }
        >
          {criticalError}
        </Alert>
      </Box>
    );
  }
  
  if (!selectedUser) {
    return (
      <Alert severity="error" sx={{ my: 2 }}>
        User not found. The user might have been deleted or you don't have permission to view it.
      </Alert>
    );
  }
  
  return (
    <Box>
      {/* Breadcrumbs */}
      <Breadcrumbs 
        separator={<NavigateNextIcon fontSize="small" />} 
        aria-label="breadcrumb"
        sx={{ mb: 3 }}
      >
        <Link
          underline="hover"
          color="inherit"
          onClick={() => navigate('/users')}
          sx={{ cursor: 'pointer' }}
        >
          Users
        </Link>
        <Typography color="text.primary">
          {selectedUser.fullName}
        </Typography>
      </Breadcrumbs>
      
      {/* Header with Actions */}
      <Box sx={{ mb: 4, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <Typography variant="h4" component="h1">
            User Details
          </Typography>
          
          <StatusChip
            label={selectedUser.active ? 'Active' : 'Inactive'}
            color={selectedUser.active ? 'success' : 'default'}
            sx={{ ml: 2 }}
          />
        </Box>
        
        <Box>
          {(canUpdate || canDelete) && (
            <>
              <Button
                variant="outlined"
                
                onClick={handleMenuOpen}
                startIcon={<MoreIcon />}
              >
                Actions
              </Button>
              
              <Menu
                anchorEl={menuAnchorEl}
                open={Boolean(menuAnchorEl)}
                onClose={handleMenuClose}
              >
                {canUpdate && (
                  <MenuItem onClick={handleEditUser}>
                    <EditIcon fontSize="small" sx={{ mr: 1 }} />
                    Edit User
                  </MenuItem>
                )}
                
                {canUpdate && (
                  <MenuItem onClick={() => handleStatusClick(!selectedUser.active)}>
                    {selectedUser.active ? (
                      <>
                        <LockIcon fontSize="small" sx={{ mr: 1 }} />
                        Deactivate User
                      </>
                    ) : (
                      <>
                        <UnlockIcon fontSize="small" sx={{ mr: 1 }} />
                        Activate User
                      </>
                    )}
                  </MenuItem>
                )}
                
                {canDelete && (
                  <MenuItem onClick={handleDeleteClick} sx={{ color: 'error.main' }}>
                    <DeleteIcon fontSize="small" sx={{ mr: 1 }} />
                    Delete User
                  </MenuItem>
                )}
              </Menu>
            </>
          )}
        </Box>
      </Box>
      
      {/* User Profile Overview */}
      <Card level={1} sx={{ mb: 4 }}>
        <Box sx={{ p: 3 }}>
          <Grid container spacing={3}>
            <Grid item xs={12} md={3}>
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                <SecureAvatar
                  src={selectedUser.photo}
                  alt={selectedUser.fullName}
                  sx={{ width: 120, height: 120, mb: 2, fontSize: 48 }}
                >
                  {getInitials(selectedUser.firstName, selectedUser.lastName)}
                </SecureAvatar>
                
                <Typography variant="h6" align="center">
                  {selectedUser.fullName}
                </Typography>
                
                <Typography variant="body2" color="textSecondary" align="center">
                  {selectedUser.email}
                </Typography>
                
                <Box sx={{ mt: 2 }}>
                  <StatusChip
                    label={USER_ROLES[selectedUser.role]?.label || selectedUser.role}
                    color={USER_ROLES[selectedUser.role]?.color || 'default'}
                  />
                </Box>
              </Box>
            </Grid>
            
            <Grid item xs={12} md={9}>
              <List>
                <ListItem>
                  <ListItemAvatar>
                    <Avatar>
                      <IDIcon />
                    </Avatar>
                  </ListItemAvatar>
                  <ListItemText
                    primary="User ID"
                    secondary={selectedUser._id}
                  />
                </ListItem>
                
                <ListItem>
                  <ListItemAvatar>
                    <Avatar>
                      <EmailIcon />
                    </Avatar>
                  </ListItemAvatar>
                  <ListItemText
                    primary="Email Address"
                    secondary={selectedUser.email}
                  />
                </ListItem>
                
                <ListItem>
                  <ListItemAvatar>
                    <Avatar>
                      <PhoneIcon />
                    </Avatar>
                  </ListItemAvatar>
                  <ListItemText
                    primary="Phone Number"
                    secondary={selectedUser.phone || 'Not provided'}
                  />
                </ListItem>
                
                <ListItem>
                  <ListItemAvatar>
                    <Avatar>
                      <BusinessIcon />
                    </Avatar>
                  </ListItemAvatar>
                  <ListItemText
                    primary="Client"
                    secondary={
                      selectedUser.clientId?.name || 
                      (typeof selectedUser.clientId === 'string' && selectedUser.clientId) || 
                      'Not assigned to a client'
                    }
                  />
                </ListItem>
                
                <ListItem>
                  <ListItemAvatar>
                    <Avatar>
                      <EventIcon />
                    </Avatar>
                  </ListItemAvatar>
                  <ListItemText
                    primary="Created At"
                    secondary={formatDate(selectedUser.createdAt)}
                  />
                </ListItem>
                
                <ListItem>
                  <ListItemAvatar>
                    <Avatar>
                      <SecurityIcon />
                    </Avatar>
                  </ListItemAvatar>
                  <ListItemText
                    primary="Account Status"
                    secondary={
                      <StatusChip
                        label={selectedUser.active ? 'Active' : 'Inactive'}
                        color={selectedUser.active ? 'success' : 'default'}
                        size="small"
                      />
                    }
                  />
                </ListItem>
              </List>
            </Grid>
          </Grid>
        </Box>
      </Card>
      
      {/* Tabs for different sections */}
      <Card level={1}>
        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tabs value={activeTab} onChange={handleTabChange} aria-label="user detail tabs">
            <Tab 
              icon={<AccountIcon />} 
              label="Permissions" 
              id="user-detail-tab-0"
              aria-controls="user-detail-tabpanel-0"
            />
            <Tab 
              icon={<BusinessIcon />} 
              label="Assigned Clients" 
              id="user-detail-tab-1"
              aria-controls="user-detail-tabpanel-1"
            />
            <Tab 
              icon={<SettingsIcon />} 
              label="User Activity" 
              id="user-detail-tab-2"
              aria-controls="user-detail-tabpanel-2"
            />
          </Tabs>
        </Box>
        
        {/* Permissions Tab */}
        <TabPanel value={activeTab} index={0}>
          <UserDetailPermissions user={selectedUser} />
        </TabPanel>
        
        {/* Assigned Clients Tab */}
        <TabPanel value={activeTab} index={1}>
          <Box sx={{ p: 2 }}>
            <Typography variant="subtitle1" gutterBottom>
              Assigned Clients
            </Typography>
            
            <Divider sx={{ mb: 3 }} />
            
            {selectedUser.assignedClients && selectedUser.assignedClients.length > 0 ? (
              <Grid container spacing={2}>
                {selectedUser.assignedClients.map((client) => (
                  <Grid item xs={12} sm={6} md={4} key={client._id || client}>
                    <Paper sx={{ p: 2 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <Avatar sx={{ mr: 2 }}>
                          <BusinessIcon />
                        </Avatar>
                        <Typography variant="subtitle2">
                          {client.name || 'Client Details Not Available'}
                        </Typography>
                      </Box>
                    </Paper>
                  </Grid>
                ))}
              </Grid>
            ) : (
              <Box>
                {['client-user', 'client-manager', 'client-admin'].includes(selectedUser.role) ? (
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <Avatar sx={{ mr: 2 }}>
                      <BusinessIcon />
                    </Avatar>
                    <Typography variant="subtitle2">
                      {selectedUser.clientId?.name || 'Primary Client Not Available'}
                    </Typography>
                  </Box>
                ) : (
                  <Typography variant="body1" color="textSecondary">
                    No clients assigned to this user.
                  </Typography>
                )}
              </Box>
            )}
          </Box>
        </TabPanel>
        
        {/* User Activity Tab */}
        <TabPanel value={activeTab} index={2}>
          <Box sx={{ p: 2 }}>
            <Typography variant="subtitle1" gutterBottom>
              User Activity
            </Typography>
            
            <Divider sx={{ mb: 3 }} />
            
            <Typography variant="body1" color="textSecondary">
              User activity tracking is not implemented in this demo.
            </Typography>
            
            <Box sx={{ mt: 2 }}>
              <Paper sx={{ p: 2 }}>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="body2" color="textSecondary">
                      Last Login
                    </Typography>
                    <Typography variant="body1">
                      {formatDate(selectedUser.lastLogin) || 'Never'}
                    </Typography>
                  </Grid>
                  
                  <Grid item xs={12} sm={6}>
                    <Typography variant="body2" color="textSecondary">
                      Created By
                    </Typography>
                    <Typography variant="body1">
                      {selectedUser.createdBy?.fullName || selectedUser.createdBy?.name || 'Unknown'}
                    </Typography>
                  </Grid>
                </Grid>
              </Paper>
            </Box>
          </Box>
        </TabPanel>
      </Card>
      
      {/* Delete Confirmation Dialog */}
      <ConfirmDialog
        open={deleteDialogOpen}
        title="Delete User"
        content="Are you sure you want to delete this user? This action cannot be undone."
        confirmText="Delete"
        cancelText="Cancel"
        onConfirm={handleDeleteConfirm}
        onCancel={() => setDeleteDialogOpen(false)}
        isDelete
        isLoading={loading}
      />
      
      {/* Status Change Confirmation Dialog */}
      <ConfirmDialog
        open={statusDialogOpen}
        title={newStatus ? "Activate User" : "Deactivate User"}
        content={newStatus 
          ? "Are you sure you want to activate this user? They will regain access to the system."
          : "Are you sure you want to deactivate this user? They will lose access to the system."
        }
        confirmText={newStatus ? "Activate" : "Deactivate"}
        cancelText="Cancel"
        onConfirm={handleStatusConfirm}
        onCancel={() => setStatusDialogOpen(false)}
        confirmButtonColor={newStatus ? "success" : "error"}
        isLoading={loading}
      />
    </Box>
  );
};

export default UserDetail;